import { DealStatsBadge } from "@/components/badges/deal-stats-badge";
import { JoyrideProvider } from "@/components/providers/joyride-provider";
import { Badge } from "@/components/ui/badge";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { useActiveOrg } from "@/hooks/use-active-org";
import { useOrgQuota } from "@/hooks/use-org-quota";
import { useSubscription } from "@/hooks/use-subscription";
import { DealTransformed } from "@/lib/schema/deal-schema";
import { cn } from "@/lib/utils";
import { useDealStatsStore } from "@/store/deal-stats-store";
import { useLayoutStore } from "@/store/layout-store";
import { TOUR_TYPES, useTourStore } from "@/store/tour-store";
import { useWorkspaceStore } from "@/store/workspace-store";
import { Bot, Clock, Menu, MessageCircle, PanelRight, X } from "lucide-react";
import React, { ReactNode, useEffect } from "react";
import { Link, Outlet } from "react-router";
import { AppSidebar } from "../sidebar/app-sidebar";
import { DiligencegptSidebar } from "../sidebar/diligencegpt-sidebar";
import { Button } from "../ui/button";

interface BaseLayoutProps {
  // Deal-specific props
  isDealLayout?: boolean;
  currentDeal?: DealTransformed;
  dealSidebar?: ReactNode;
  diligenceGptSidebar?: ReactNode;
  dealSidebarCollapsed?: boolean;
  onDealSidebarToggle?: () => void;
  chatOpen?: boolean;
  onChatToggle?: () => void;
  mobileMenuOpen?: boolean;
  onMobileMenuToggle?: () => void;

  // Additional header content
  additionalHeaderContent?: ReactNode;

  // Outlet context
  outletContext?: unknown;

  // Loading state
  isLoading?: boolean;
  loadingContent?: ReactNode;

  disableMobileTopBar?: boolean;
}

export const BaseLayout = ({
  isDealLayout = false,
  dealSidebar,
  currentDeal,
  diligenceGptSidebar,
  dealSidebarCollapsed,
  onDealSidebarToggle,
  chatOpen,
  onChatToggle,
  mobileMenuOpen,
  onMobileMenuToggle,
  additionalHeaderContent,
  outletContext,
  isLoading,
  loadingContent,
  disableMobileTopBar = false,
}: BaseLayoutProps) => {
  const { activeWorkspace } = useWorkspaceStore();
  const { isTrialing, trialDaysRemaining } = useSubscription();
  const { fetchDealStats } = useDealStatsStore();
  const { billingHref: billingLink } = useActiveOrg();
  const { quota } = useOrgQuota();

  const isReportPanelFullscreen = useLayoutStore(
    (s) => s.isReportPanelFullscreen
  );

  const { startTour } = useTourStore();

  // Fetch deal stats when workspace changes
  useEffect(() => {
    if (activeWorkspace?._id) {
      fetchDealStats(activeWorkspace._id);
    }
  }, [activeWorkspace?._id, fetchDealStats]);

  // Clerk handles logout automatically through UserButton

  useEffect(() => {
    startTour(TOUR_TYPES.SIDEBAR);
  }, [startTour]);

  // Fix for overflow issue caused by third-party libraries
  useEffect(() => {
    const fixOverflow = () => {
      const mainElement = document.querySelector("main");
      if (mainElement && mainElement.style.overflow === "initial") {
        mainElement.style.overflow = "";
        mainElement.style.overflowX = "hidden";
        mainElement.style.overflowY = "auto";
      }
    };

    // Initial fix
    fixOverflow();

    // Monitor for changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "style"
        ) {
          fixOverflow();
        }
      });
    });

    const mainElement = document.querySelector("main");
    if (mainElement) {
      observer.observe(mainElement, {
        attributes: true,
        attributeFilter: ["style"],
      });
    }

    return () => observer.disconnect();
  }, [isDealLayout]);

  return (
    <JoyrideProvider>
      <SidebarProvider defaultOpen={isDealLayout ? false : undefined}>
        {!isDealLayout && !isReportPanelFullscreen && <AppSidebar />}

        {/* App Sidebar - Desktop Only for deals, always visible for sidebar layout */}
        {isDealLayout && !isReportPanelFullscreen && (
          <AppSidebar collapsible="icon" className="hidden md:block" />
        )}

        {/* Deal Sidebar - Desktop (only for deal layout) */}
        {isDealLayout && dealSidebar && (
          <div className="hidden md:block">{dealSidebar}</div>
        )}

        {/* Mobile Menu Overlay - Deal Sidebar Only (only for deal layout) */}
        {isDealLayout && mobileMenuOpen !== undefined && (
          <div
            className={`lg:hidden fixed inset-0 z-40 transition-visibility duration-300 ${mobileMenuOpen ? "visible" : "invisible"
              }`}
          >
            <div
              className={`fixed inset-0 bg-black/50 transition-opacity duration-300 ${mobileMenuOpen ? "opacity-100" : "opacity-0"
                }`}
              onClick={onMobileMenuToggle}
            />
            <div
              className={`fixed left-0 top-0 h-full w-64 bg-background transform transition-transform duration-300 ease-in-out ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
                }`}
            >
              {dealSidebar &&
                React.cloneElement(dealSidebar as React.ReactElement, {
                  onMobileMenuToggle,
                })}
            </div>
          </div>
        )}

        <main
          className={cn(
            "flex-1 flex flex-col h-dvh overflow-hidden"
          )}
        >
          <header className={cn(
            "h-14 border-b flex shrink-0 items-center px-4 justify-between w-full sticky top-0 z-50 bg-white dark:bg-zinc-950 shadow-sm isolate",
            !isDealLayout && (disableMobileTopBar ? "hidden" : "md:hidden")
          )}>
            <div className="flex items-center gap-4 min-w-0 flex-1">
              {isDealLayout ? (
                dealSidebar ? (
                  <>
                    <div className="block md:hidden">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={onMobileMenuToggle}
                        data-tour="mobile-menu-button"
                      >
                        <Menu className="h-5 w-5" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={onDealSidebarToggle}
                        className="hidden md:flex"
                      >
                        <PanelRight
                          className={cn(
                            "h-4 w-4 transition-transform duration-300 text-black",
                            dealSidebarCollapsed && "rotate-180"
                          )}
                        />
                      </Button>
                    </div>
                  </>
                ) : (
                  <SidebarTrigger className="md:hidden" />
                )
              ) : (
                <>
                  <SidebarTrigger />
                  <div className="text-sm font-medium text-foreground truncate min-w-0">
                    {activeWorkspace?.opportunity_name ?? ""}
                  </div>
                </>
              )}
              {additionalHeaderContent}
            </div>

            <div className="flex items-center gap-4 shrink-0">
              <div className="flex items-center gap-2">
                {isTrialing && trialDaysRemaining !== null && (
                  <Link
                    to={`/${activeWorkspace?._id}/settings/billing`}
                    className="hidden sm:block"
                  >
                    <Badge
                      variant="secondary"
                      className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800 h-8"
                      alt-text="Trial days remaining"
                    >
                      <Clock className="h-3 w-3 mr-1" />
                      <span className="text-xs font-medium">
                        {trialDaysRemaining}{" "}
                        {trialDaysRemaining === 1 ? "day" : "days"} left
                        <span className="hidden lg:inline"> in trial</span>
                      </span>
                    </Badge>
                  </Link>
                )}

                {quota && (
                  <DealStatsBadge
                    billingLink={billingLink}
                    tracked={quota.premium_deals_used}
                    cap={quota.effective_premium_deals_cap ?? quota.premium_deals_cap}
                  />
                )}
              </div>

              {isDealLayout && onChatToggle && (
                <div className="2xl:hidden">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={onChatToggle}
                    data-tour="mobile-chat-button"
                  >
                    <MessageCircle className="h-5 w-5" />
                  </Button>
                </div>
              )}
              {/* Profile avatar lives in the sidebar (see AppSidebar footer). */}
            </div>
          </header>

          <div className={cn(
            "flex-1 overflow-y-auto overflow-x-hidden",
            isDealLayout && "px-2 sm:px-4 py-2 sm:py-4"
          )}>
            {isLoading ? loadingContent : <Outlet context={outletContext} />}
          </div>
        </main>

        {/* DiligenceGPT Sidebar sections (only for deal layout) */}
        {isDealLayout && diligenceGptSidebar && (
          <>
            {/* Desktop */}
            <div className="hidden 2xl:block">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 bg-[#171717]">
                <div className="flex items-center gap-3">
                  <div className="flex aspect-square h-8 w-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                    <Bot className="h-4 w-4" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-gray-100">
                      DiligenceGPT Chat
                    </h2>
                    <p className="text-xs text-gray-400">
                      Ask questions about this company
                    </p>
                  </div>
                </div>
              </div>
              <DiligencegptSidebar
                side="right"
                collapsible="none"
                className="h-[calc(100vh-52px)] overflow-y-hidden w-[280px] md:w-[180px] lg:w-[360px] xl:w-[400px] 2xl:w-[440px] bg-[#171717]"
                currentDeal={currentDeal as DealTransformed}
              />
            </div>

            {/* Tablet */}
            <div
              className={`hidden md:block 2xl:hidden fixed inset-y-0 right-0 z-50 transition-all duration-300 ease-in-out ${chatOpen ? "translate-x-0" : "translate-x-full"
                }`}
            >
              <div className="h-full flex">
                <div
                  className={`transition-opacity duration-300 ${chatOpen ? "opacity-100" : "opacity-0"
                    }`}
                  onClick={onChatToggle}
                />
                <div className="h-full w-[400px] bg-[#171717] shadow-xl">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
                    <div className="flex items-center gap-3">
                      <div className="flex aspect-square h-8 w-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                        <Bot className="h-4 w-4" />
                      </div>
                      <div>
                        <h2 className="font-semibold text-gray-100">
                          DiligenceGPT Chat
                        </h2>
                        <p className="text-xs text-gray-400">
                          Ask questions about this company
                        </p>
                      </div>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={onChatToggle}
                      className="text-gray-400 hover:text-white hover:bg-transparent"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                  <DiligencegptSidebar
                    side="right"
                    collapsible="none"
                    className="h-[calc(100%-52px)] w-full bg-[#171717]"
                    currentDeal={currentDeal as DealTransformed}
                  />
                </div>
              </div>
            </div>

            {/* Mobile */}
            <div
              className={`md:hidden fixed inset-0 z-50 transition-visibility duration-300 ${chatOpen ? "visible" : "invisible"
                }`}
            >
              <div
                className={`fixed inset-0 bg-black/50 transition-opacity duration-300 ease-in-out ${chatOpen ? "opacity-100" : "opacity-0"
                  }`}
                onClick={onChatToggle}
              />
              <div
                className={`fixed inset-0 bg-background transform transition-all duration-300 ease-in-out ${chatOpen
                  ? "translate-y-0 opacity-100"
                  : "translate-y-full opacity-0"
                  }`}
              >
                <div className="flex items-center justify-between px-4 py-3 border-b bg-black border-gray-800">
                  <div className="flex items-center gap-3">
                    <div className="flex aspect-square h-8 w-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                      <Bot className="h-4 w-4" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-gray-100">
                        DiligenceGPT Chat
                      </h2>
                      <p className="text-xs text-gray-400">
                        Ask questions about this company
                      </p>
                    </div>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={onChatToggle}
                    className="text-gray-400 hover:text-white hover:bg-transparent"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
                {diligenceGptSidebar}
              </div>
            </div>
          </>
        )}
      </SidebarProvider>
    </JoyrideProvider>
  );
};
